import { Avatar as NextUIAvatar, AvatarProps as NextUIAvatarProps } from '@nextui-org/react';
import styled from 'styled-components';

import { ThemeProp } from '../../theme/Themes';

export enum AvatarVariant {
  XLARGE = 'xlarge',
  LARGE = 'large',
  MEDIUM = 'medium',
  SMALL = 'small',
  XSMALL = 'xsmall',
}

interface AvatarProps extends NextUIAvatarProps {
  borderRadius?: string;
  border?: string;
  fontSize?: string;
  fontColor?: string;
  fontWeight?: string;
  lineHeight?: string;
  backgroundColor?: string;
  width?: string;
  height?: string;
  variant?: AvatarVariant;
}

const getBorderRadius = ({ squared, borderRadius, theme }: AvatarProps & ThemeProp): string =>
  !squared ? null : borderRadius || theme.borderRadius;

const getDimensions = ({ variant }: AvatarProps): string => {
  switch (variant) {
    case AvatarVariant.XLARGE:
      return '3.5rem';
    case AvatarVariant.LARGE:
      return '2.5rem';
    case AvatarVariant.MEDIUM:
      return '2rem';
    case AvatarVariant.SMALL:
      return '1.5rem';
    case AvatarVariant.XSMALL:
      return '1.25rem';
    default:
      return 'auto';
  }
};

const getFontSize = ({ variant, theme }: AvatarProps & ThemeProp) => {
  switch (variant) {
    case AvatarVariant.XLARGE:
      return theme.fontSize['2xl'];
    case AvatarVariant.LARGE:
      return theme.fontSize.xl;
    case AvatarVariant.MEDIUM:
      return theme.fontSize.lxl;
    case AvatarVariant.SMALL:
      return theme.fontSize.ml;
    case AvatarVariant.XSMALL:
      return theme.fontSize.xxs;
    default:
      return theme.fontSize.l;
  }
};

const getLineHeight = ({ variant, theme }: AvatarProps & ThemeProp) => {
  switch (variant) {
    case 'large':
      return theme.lineHeight.s;
    case 'medium':
      return theme.lineHeight.ml;
    case 'small':
      return theme.lineHeight.xxs;
    default:
      return theme.lineHeight.ml;
  }
};

const Avatar = styled(NextUIAvatar).attrs((props: AvatarProps) => ({
  ...props,
  css: {
    '.nextui-avatar-img': {
      opacity: '1',
    },
    ...props.css,
  },
}))<AvatarProps>`
  width: ${(props: AvatarProps) => props.width ?? getDimensions(props)};
  height: ${(props: AvatarProps) => props.height ?? getDimensions(props)};
  min-width: ${(props: AvatarProps) => props.width ?? getDimensions(props)};
  min-height: ${(props: AvatarProps) => props.height ?? getDimensions(props)};
  border-radius: ${(props: ThemeProp & AvatarProps) => getBorderRadius(props)};
  flex-shrink: 0;

  .nextui-avatar-bg,
  .nextui-avatar-img {
    opacity: 1;
    /* If there's an image (src), then do not show any background */
    background-color: ${(props: ThemeProp & AvatarProps) =>
      props.src ? 'transparent' : props.backgroundColor || props.theme.colors.main.c100};
    border-radius: ${(props: ThemeProp & AvatarProps) => getBorderRadius(props)};
    border: ${(props: AvatarProps) => props.border || 'none'};
  }
  .nextui-avatar-text {
    -webkit-text-stroke: 0px;
    width: fit-content;
    height: fit-content;
    color: ${(props: ThemeProp & AvatarProps) => props.fontColor || props.theme.colors.main.c600};
    font-size: ${(props: AvatarProps & ThemeProp) => props.fontSize || getFontSize(props)};
    font-weight: ${(props: AvatarProps & ThemeProp) => props.fontWeight || props.theme.fontWeight.medium};
    line-height: ${(props: AvatarProps & ThemeProp) => props.lineHeight || getLineHeight(props)};
  }
`;

export default Avatar;
